import { AxiosResponse } from 'axios'
import createHttpClient from '@/api/httpClient'
import { ApiResponse } from '@/api/api.d'
import {
  AccountInformationData,
  AccountUpdateParams,
  AccountUpdateResponse,
  AvatarUpdateParams,
} from '@/api/account/account.d'
import {
  AddressDeleteParams,
  AddressInformationData,
  AddressParams,
  AddressResponse,
  CheckAppointmentResponse,
} from './address'

const httpClient = createHttpClient()

export interface country {
  id: string
  label: string
  code?: string
  isAllowedForBusiness: boolean
}

export const getInformations = async (): Promise<
  ApiResponse<AccountInformationData>
> => {
  const response: AxiosResponse<AccountInformationData> =
    await httpClient.get<AccountInformationData>('/account/informations')
  return {
    status: response.status,
    data: response.data,
  }
}

export const updateInformations = async (
  params: AccountUpdateParams,
): Promise<ApiResponse<AccountUpdateResponse>> => {
  const response: AxiosResponse<AccountUpdateResponse> =
    await httpClient.put<AccountUpdateResponse>('/account/informations', params)
  return {
    status: response.status,
    data: response.data,
  }
}

export const getAddresses = async (): Promise<
  ApiResponse<AddressInformationData[]>
> => {
  try {
    const response: AxiosResponse<AddressInformationData[]> =
      await httpClient.get<AddressInformationData[]>('/address/', {
        headers: { 'Content-Type': 'application/json' },
      })
    return {
      status: response.status,
      data: response.data,
    }
  } catch (_) {}
}

export const updateAddress = async (
  params: AddressParams,
): Promise<ApiResponse<AddressResponse>> => {
  try {
    const { id, ...payload } = params

    const response: AxiosResponse<AddressResponse> =
      await httpClient.put<AddressResponse>(`/address/${params.id}`, payload, {
        headers: { 'Content-Type': 'application/json' },
      })
    return {
      status: response.status,
      data: response.data,
    }
  } catch (err) {
    return {
      status: err.status,
      data: err.response.data,
    }
  }
}

export const storeAddress = async (
  params: AddressParams,
): Promise<ApiResponse<AddressResponse>> => {
  try {
    const response: AxiosResponse<AddressResponse> =
      await httpClient.post<AddressResponse>('/address/', params, {
        headers: { 'Content-Type': 'application/json' },
      })
    return {
      status: response.status,
      data: response.data,
    }
  } catch (err) {
    return {
      status: err.status,
      data: err.response.data,
    }
  }
}

export const deleteAddress = async (
  id: string,
): Promise<ApiResponse<AddressResponse[]>> => {
  const response: AxiosResponse<AddressResponse[]> = await httpClient.delete<
    AddressResponse[]
  >('/address/' + id, {
    headers: {
      'Content-Type': 'application/json',
    },
  })
  return {
    status: response.status,
    data: response.data,
  }
}

export const checkAppointment = async (
  id: string,
): Promise<ApiResponse<CheckAppointmentResponse[]>> => {
  const response: AxiosResponse<CheckAppointmentResponse[]> =
    await httpClient.get<CheckAppointmentResponse[]>(
      '/address/' + id + '/has-patient',
      {
        headers: {
          'Content-Type': 'application/json',
        },
      },
    )
  return {
    status: response.status,
    data: response.data,
  }
}

export const updatePassword = async (payload: any) => {
  try {
    const response: AxiosResponse = await httpClient.put(
      '/account/security',
      payload,
    )
    return {
      status: response.status,
      data: response.data,
    }
  } catch (error) {
    return {
      status: error.status,
      data: error.response.data,
    }
  }
}

export const removeAvatar = async () => {
  const response: AxiosResponse = await httpClient.delete(
    '/account/remove-avatar',
  )
  return response
}

export const updateAvatar = async (params: AvatarUpdateParams) => {
  const response: AxiosResponse = await httpClient.put(
    '/account/update-avatar',
    params,
  )
  return response
}

export const getCountries = async (
  onlyBusiness?: boolean, // facultatif : true | false | undefined
): Promise<country[]> => {
  let url = '/country'

  if (onlyBusiness !== undefined) {
    url += `?business=${onlyBusiness}`
  }

  const response: AxiosResponse<country[]> = await httpClient.get(url, {
    headers: { 'Content-Type': 'application/json' },
  })

  return response.data
}

export const tvaByCountry = async (countryCompany: string): Promise<number> => {
  if (!countryCompany) return 0

  const { data: tvaRate }: AxiosResponse = await httpClient.get(
    `/utils/tva?country=${countryCompany}`,
    { headers: { 'Content-Type': 'application/json' } },
  )

  return tvaRate
}
